import { useState, useCallback, useEffect } from 'react';
import { useNotification } from '@/stores/notificationStore';
import { adminService } from '@/services/admin/HttpAdminAdapter';
import type {
  User,
  UpdateUserRequest,
  Organization,
} from '@/services/admin/AdminService';
import Logger from '@/utils/logger';

const logger = Logger.create('useUsers');

/**
 * Hook for fetching and managing users with CRUD operations
 * Provides loading states and error handling
 */
export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { showNotification } = useNotification();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.listUsers();
      setUsers(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch users';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrganizations = useCallback(async () => {
    try {
      const data = await adminService.listOrganizations();
      setOrganizations(data);
    } catch (err) {
      // Organizations are optional for display, so we just log but don't show error to user
      logger.warn('Failed to fetch organizations (non-blocking):', err);
    }
  }, []);

  const updateUser = useCallback(
    async (uid: string, data: UpdateUserRequest): Promise<User | null> => {
      setActionLoading(true);
      try {
        const updated = await adminService.updateUser(uid, data);
        setUsers((prev) =>
          prev.map((user) => (user.uid === uid ? updated : user))
        );
        showNotification('User updated successfully', 'success');
        return updated;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update user';
        showNotification(errorMessage, 'error');
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [showNotification]
  );

  const deleteUser = useCallback(
    async (uid: string): Promise<boolean> => {
      setActionLoading(true);
      try {
        await adminService.deleteUser(uid);
        setUsers((prev) =>
          prev.map((user) =>
            user.uid === uid ? { ...user, is_active: false } : user
          )
        );
        showNotification('User deactivated successfully', 'success');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to deactivate user';
        showNotification(errorMessage, 'error');
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [showNotification]
  );

  // Fetch on mount
  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, [fetchUsers, fetchOrganizations]);

  return {
    users,
    organizations,
    loading,
    error,
    actionLoading,
    fetchUsers,
    updateUser,
    deleteUser,
  };
};

export default useUsers;
